import moment from "moment"
import React from "react"
import ReactTooltip from "react-tooltip"
import styled, { css } from "styled-components"

import assertNever from "assert-never"
import { WebLNProvider } from "webln"
import { Actions, SbDispatch } from "../reducer/reducer"

import {
  API,
  EditorState,
  isRestAPI,
  RestAPI,
  RestEditorState,
  SocketAPI,
  SocketEditorState
} from "../reducer/common.types"
import { Button, device, HeaderText } from "../styles"
import { Card } from "./Card"
import { MessageEditorDropdowns } from "./editor/message.editor.dropdowns"
import { RestEditor } from "./editor/rest.editor"
import { WsMessageEditor } from "./editor/ws.message.editor"

const isRestProps = (
  props: MakeRequestsProps
): props is MakeRequestsRestProps => isRestAPI(props.api)

export interface MakeRequestsSocketProps
  extends CommonProps<SocketEditorState<SocketAPI>> {
  refundInvoice: string
  nameMeBetterToBeSent: string
  connected: boolean
  /**
   * Currently active socket
   */
  socket: WebSocket | undefined
}

export interface MakeRequestsRestProps
  extends CommonProps<RestEditorState<RestAPI>> {
  /**
   * Currently active WebLN provider, if present
   */
  webLn: WebLNProvider | undefined
}

interface CommonProps<E extends EditorState<API>> {
  editorState: E
  api: E["api"]

  /** do we have GET requests in flight? */
  pendingRequest: boolean

  dispatch: SbDispatch
}

export type MakeRequestsProps = MakeRequestsSocketProps | MakeRequestsRestProps

const PayloadDiv = styled(Card)`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-around;

  grid-area: build-request;

  overflow-x: scroll;

  @media ${device.smallerThen.laptop} {
    min-height: 50vw;
  }
`

const PayloadContentDiv = styled.div`
  overflow-y: scroll;
`

const RequestButton = styled(Button)`
  min-height: 20%;
`

const SendButton = styled(RequestButton)`
  flex: 1;
  color: white;

  ${({ disabled }) =>
    disabled
      ? css`
          background-color: lightgrey;
        `
      : css`
          background-color: #2e5bda;
          border-color: #2e5bda;
        `}
`

const ButtonDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-content: space-around;
  margin-bottom: 0.5em;
`

export const MakeRequests: React.SFC<MakeRequestsProps> = props => {
  const { dispatch } = props

  function getSendButtonIsDisabled() {
    if (isRestProps(props)) {
      return false
    } else {
      return !props.connected
    }
  }
  const sendButtonIsDisabled = getSendButtonIsDisabled()

  function getToolTip(): string {
    if (isRestProps(props)) {
      return ""
    } else {
      if (props.connected) {
        return "Please enter valid JSON in the 'Payload' field"
      } else {
        return "You must be connected to an API before you can send requests"
      }
    }
  }
  const tooltip = getToolTip()

  const sendButton = (
    <React.Fragment key={"send"}>
      <SendButton
        data-tip={true}
        data-for="send-button"
        onClick={() => {
          if (isRestProps(props)) {
            Actions.sendRestPayload({
              dispatch,
              ln: props.webLn,
              payload: props.editorState
            })
          } else {
            Actions.sendWsPayload({
              payload: props.nameMeBetterToBeSent,
              websocket: props.socket
            })
            dispatch({
              type: "ADD_MESSAGE",
              date: moment(),
              kind: "sent",
              language: "language-json",
              text: props.nameMeBetterToBeSent
            })
          }
        }}
        disabled={sendButtonIsDisabled}
      >
        Send
      </SendButton>
      {sendButtonIsDisabled ? (
        <ReactTooltip id="send-button" effect="solid">
          {tooltip}
        </ReactTooltip>
      ) : null}
    </React.Fragment>
  )

  const RequestEditor = getRequestEditor({
    dispatch,
    pendingRequest: props.pendingRequest,
    state: props.editorState
  })

  return (
    <PayloadDiv>
      <HeaderText>Payload</HeaderText>
      <PayloadContentDiv>
        <MessageEditorDropdowns
          editable={isRestProps(props) || props.connected}
          dispatch={dispatch}
          state={props.editorState}
        />
        {RequestEditor}
      </PayloadContentDiv>
      <ButtonDiv>{sendButton}</ButtonDiv>
    </PayloadDiv>
  )
}

interface GetRequestEditorArgs {
  state: EditorState<API>
  dispatch: SbDispatch
  /** If a GET request is in flight or not */
  pendingRequest: boolean
}

const getRequestEditor: (args: GetRequestEditorArgs) => JSX.Element = ({
  state,
  dispatch,
  pendingRequest
}) => {
  switch (state.api) {
    case "NBA":
    case "futures":
    case "spot": {
      return (
        <WsMessageEditor
          dispatch={dispatch}
          state={state}
          setState={newState =>
            dispatch({ type: "SET_EDITOR_STATE", state: newState })
          }
        />
      )
    }
    case "NFL":
    case "historical": {
      return (
        <RestEditor
          setState={newState =>
            dispatch({ type: "SET_EDITOR_STATE", state: newState })
          }
          state={state}
          pendingRequest={pendingRequest}
        />
      )
    }
    default: {
      return assertNever(state)
    }
  }
}
